import { useId } from 'react'
import type { ReactNode } from 'react'
import { ChartFigure, ChartLegend } from '../ChartFigure/ChartFigure'
import {
  bands,
  niceScale,
  plot,
  plotBaseline,
  plotHeight,
  plotWidth,
  sum,
  thinLabels,
} from '../geometry'
import { formatAxis, formatCount } from '../../format'
import palette from '../series.module.css'
import styles from './BarChart.module.css'

/**
 * Bars, grouped or stacked.
 *
 * Hand-rolled, like every chart here, for the two reasons the conventions give:
 * a charting library ships between fifty and two hundred kilobytes to draw four
 * of these and arrives with its own colour and type opinions that then have to
 * be overridden back to the brand one property at a time; and a chart this
 * simple is shorter as SVG than the configuration object that would describe it
 * to a library.
 *
 * The one thing here that is not obvious is the texture. Stacked segments touch
 * each other, so hue is the only thing separating them, and the design system's
 * rule is that meaning is never carried by hue alone. Each series therefore gets
 * a motif as well as a colour: solid, then diagonal, dotted, horizontal,
 * cross-hatched, vertical. The pattern is a genuine second channel rather than
 * decoration, and it survives being printed in black and white, which is what
 * happens to a quarterly report on its way to a donor.
 *
 * A single series has nothing to distinguish itself from, so it draws solid.
 */

export interface BarSeries {
  key: string
  label: string
  /** One value per category, in the same order as `categories`. */
  values: readonly number[]
}

export interface BarChartProps {
  title: string
  description?: ReactNode
  categories: readonly string[]
  series: readonly BarSeries[]
  layout?: 'grouped' | 'stacked'
  /** Heading for the category column of the text alternative. */
  categoryHeading?: string
  /** Adds a total column to the text alternative. Stacked charts want one. */
  totalHeading?: string
  tableVisible?: boolean
  /** Off for a chart that will only ever be read on screen in colour. */
  texture?: boolean
  locale?: string
  className?: string
}

export function BarChart({
  title,
  description,
  categories,
  series,
  layout = 'grouped',
  categoryHeading = '',
  totalHeading,
  tableVisible = false,
  texture,
  locale,
  className,
}: BarChartProps) {
  const patternPrefix = useId()
  const textured = (texture ?? true) && series.length > 1

  const columnTotals = categories.map((_, index) =>
    sum(series.map((entry) => entry.values[index] ?? 0)),
  )
  const highest =
    layout === 'stacked'
      ? Math.max(0, ...columnTotals)
      : Math.max(0, ...series.flatMap((entry) => entry.values))

  const scale = niceScale(highest)
  const toY = (value: number) => plotBaseline - (value / scale.max) * plotHeight

  const slots = bands(categories.length, plot.left, plotWidth)
  const visibleLabels = thinLabels(categories, 8)

  return (
    <ChartFigure
      title={title}
      description={description}
      tableVisible={tableVisible}
      className={className}
      legend={
        <ChartLegend entries={series.map((entry) => ({ label: entry.label }))} />
      }
      table={{
        columns: [
          categoryHeading,
          ...series.map((entry) => entry.label),
          ...(totalHeading ? [totalHeading] : []),
        ],
        rows: categories.map((category, index) => ({
          header: category,
          cells: [
            ...series.map((entry) => formatCount(entry.values[index] ?? 0, locale)),
            ...(totalHeading ? [formatCount(columnTotals[index] ?? 0, locale)] : []),
          ],
        })),
      }}
    >
      <svg viewBox={`0 0 ${plot.width} ${plot.height}`}>
        {textured ? (
          <defs>
            {series.map((entry, index) => (
              <Motif key={entry.key} id={`${patternPrefix}-${index}`} index={index} />
            ))}
          </defs>
        ) : null}

        {/* Gridlines first, so every bar is drawn over them. */}
        <g>
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={plot.left}
                y1={toY(tick)}
                x2={plot.width - plot.right}
                y2={toY(tick)}
                className={tick === 0 ? styles.axis : styles.grid}
              />
              <text x={plot.left - 8} y={toY(tick)} className={styles.tick} textAnchor="end">
                {formatAxis(tick, locale)}
              </text>
            </g>
          ))}
        </g>

        {categories.map((category, categoryIndex) => {
          const slot = slots[categoryIndex]
          if (!slot) return null

          const inner =
            layout === 'grouped'
              ? bands(series.length, slot.start, slot.width, 0.12)
              : series.map(() => ({ start: slot.start, width: slot.width, centre: slot.centre }))

          let stackTop = 0

          return (
            <g key={category}>
              {series.map((entry, seriesIndex) => {
                const value = entry.values[categoryIndex] ?? 0
                const bar = inner[seriesIndex]
                if (!bar) return null

                const top = layout === 'stacked' ? toY(stackTop + value) : toY(value)
                const bottom = layout === 'stacked' ? toY(stackTop) : plotBaseline
                if (layout === 'stacked') stackTop += value

                return (
                  <rect
                    key={entry.key}
                    x={bar.start}
                    y={Math.min(top, bottom)}
                    width={bar.width}
                    height={Math.abs(bottom - top)}
                    className={[palette.series, textured ? undefined : palette.fill]
                      .filter(Boolean)
                      .join(' ')}
                    data-series={seriesIndex}
                    fill={textured ? `url(#${patternPrefix}-${seriesIndex})` : undefined}
                  />
                )
              })}
            </g>
          )
        })}

        {visibleLabels.map((label, index) => {
          const slot = slots[index]
          if (label === null || !slot) return null
          return (
            <text
              key={`${label}-${index}`}
              x={slot.centre}
              y={plot.height - 10}
              className={styles.tick}
              textAnchor="middle"
            >
              {label}
            </text>
          )
        })}
      </svg>
    </ChartFigure>
  )
}

/**
 * One series' fill: its colour, and a motif over it.
 *
 * The colour arrives the same way it does everywhere else in the charts, from
 * `data-series` on the element and the rules in series.module.css, so the
 * pattern and the legend swatch that explains it cannot end up different
 * colours. The motif is stroked in the page's own surface colour, so it reads
 * as texture cut out of the bar rather than as a second colour laid on top.
 */
function Motif({ id, index }: { id: string; index: number }) {
  const motif = index % 6

  return (
    <pattern
      id={id}
      width="8"
      height="8"
      patternUnits="userSpaceOnUse"
      className={palette.series}
      data-series={index}
    >
      <rect width="8" height="8" className={palette.fill} />
      {motif === 1 ? <path d="M0 8L8 0M-2 2L2 -2M6 10L10 6" className={styles.motif} /> : null}
      {motif === 2 ? (
        <>
          <circle cx="2" cy="2" r="1" className={styles.motifDot} />
          <circle cx="6" cy="6" r="1" className={styles.motifDot} />
        </>
      ) : null}
      {motif === 3 ? <path d="M0 2h8M0 6h8" className={styles.motif} /> : null}
      {motif === 4 ? <path d="M0 0L8 8M8 0L0 8" className={styles.motif} /> : null}
      {motif === 5 ? <path d="M2 0v8M6 0v8" className={styles.motif} /> : null}
    </pattern>
  )
}
